import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAPMBlueprint } from "@/queries/apm";
import { useSessionData } from "@/stores/SessionDataContext";
import { Circle, MapPin, Train } from "lucide-react";

function formatType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function APM() {
  const sessionData = useSessionData();

  const {
    data: apmData,
    isLoading,
    error,
  } = useAPMBlueprint(sessionData.code_iata);

  const customPrefix = apmData?.custom_prefix || "APM";

  if (isLoading) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground">
          Loading {customPrefix} blueprint...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">
              No {customPrefix} system configured for this airport.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!apmData) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Train className="h-6 w-6" />
        <h2 className="text-xl font-semibold">{apmData.name}</h2>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5" />
              Stations ({apmData.stations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {apmData.stations.map((station) => (
                <div
                  key={station.id}
                  className="flex items-start justify-between p-3 rounded-lg border bg-card"
                >
                  <div className="flex-1">
                    <div className="font-medium">{station.name}</div>
                    <div className="text-sm text-muted-foreground">
                      Code: {station.id}
                    </div>
                  </div>
                  <div className="ml-2">
                    <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold">
                      {formatType(station.type)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Circle className="h-5 w-5" />
              Tracks ({apmData.tracks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {apmData.tracks.map((track) => (
                <div
                  key={track.id}
                  className="p-3 rounded-lg border bg-card space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium">Track {track.id}</div>
                    <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold">
                      {formatType(track.type)}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Route: {track.route.join(" → ")}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Train className="h-5 w-5" />
              Vehicles ({apmData.vehicles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {apmData.vehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  className="p-3 rounded-lg border bg-card space-y-1"
                >
                  <div className="font-medium">Vehicle {vehicle.id}</div>
                  <div className="text-sm text-muted-foreground">
                    Capacity: {vehicle.capacity} passengers
                  </div>
                  {vehicle.assigned_track && (
                    <div className="text-sm text-muted-foreground">
                      Assigned: Track {vehicle.assigned_track}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
